import React, {ChangeEvent, FC, useMemo, useState} from "react";
import {T} from "@tolgee/react";
import {useFieldArray, useForm} from "react-hook-form";
import parsePath from "parse-filepath";
import {isEqual} from "lodash";
import FileIcon from "view/components/FileIcon";
import DeleteIcon from "@material-ui/icons/Delete";
import AttachmentIcon from "@material-ui/icons/Attachment";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@material-ui/core";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import DataRow from "view/components/DataRow";
import type {Extension} from "ts/api/attachmentFile";
import {ProductNoteAttachment} from "ts/api/product";
import {attachmentFileFormats} from "constraints/general";
import formatBytes from "tools/formats/formatBytes";
import {buildNoteAttachment} from "view/components/ShoppingCart/noteAttachment";
import useStyles from "./styles";

type ProductAttachmentsModalProps = {
  isOpen: boolean;
  attachments: ProductNoteAttachment[];
  isFirstAttachment: boolean;
  onClose: () => void;
  onSubmit: (attachments: ProductNoteAttachment[]) => void;
};

const ProductAttachmentsModal: FC<ProductAttachmentsModalProps> = ({
  isOpen,
  attachments,
  isFirstAttachment,
  onClose,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const classes = useStyles();
  const form = useForm<{attachments: ProductNoteAttachment[]}>({
    mode: "all",
    defaultValues: {attachments},
  });
  const {control} = form;
  const {fields, remove, append} = useFieldArray({
    control,
    name: "attachments",
    keyName: "formId",
  });
  const isSubmitDisabled: boolean = useMemo(
    () => isEqual(attachments, fields),
    [attachments, fields]
  );

  const onRemoveAttachment = (attachmentIdx: number) => {
    remove(attachmentIdx);
  };
  const onAddAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";

    if (!file) {
      return;
    }

    const attachment = await buildNoteAttachment(file);

    if (attachment) {
      append(attachment);
    }
  };

  const renderActionPanel = (attachmentIdx: number) => {
    return (
      <IconButton onClick={() => onRemoveAttachment(attachmentIdx)}>
        <DeleteIcon htmlColor={theme.palette.contrastIcon.main} />
      </IconButton>
    );
  };

  const renderAttachment = (attachment: ProductNoteAttachment, idx: number) => {
    const ext = parsePath(attachment.title).ext.slice(1) as Extension;
    return (
      <DataRow
        classes={{value: classes.fileNameValue}}
        key={attachment.id}
        label={`${ext}, ${formatBytes(attachment?.size ?? 0)}`}
        value={attachment.title}
        icon={<FileIcon extension={attachment.extension} />}
        last={idx === fields.length - 1}
        actionPanel={renderActionPanel(idx)}
      />
    );
  };

  const renderSubmitButtonIcon = () => {
    if (!isLoading) {
      return null;
    }
    return (
      <CircularProgress size={18} className={classes.submitButtonEndIcon} />
    );
  };

  const handleSubmit = () => {
    setIsLoading(true);
    onSubmit(fields as ProductNoteAttachment[]);
  };

  const isSubmitButtonDisabled =
    isLoading || (!isFirstAttachment && isSubmitDisabled);

  return (
    <FfairModal open={isOpen} onClose={onClose}>
      <FfairPaper className={classes.root}>
        <Typography className={classes.title}>
          <T keyName="common.shopping_cart.add_attachment" />
        </Typography>
        {(fields as ProductNoteAttachment[]).map(renderAttachment)}
        <Box className={classes.actions}>
          <Box>
            <input
              accept={attachmentFileFormats}
              id="browse-attachment-modal"
              type="file"
              multiple={false}
              onChange={onAddAttachment}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="browse-attachment-modal">
              <IconButton component="span">
                <AttachmentIcon htmlColor={theme.palette.contrastIcon.main} />
              </IconButton>
            </label>
          </Box>
          <Box>
            <Button onClick={onClose} color="primary">
              <T keyName="common.action.cancel" />
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={isSubmitButtonDisabled}
              className={classes.submitButton}
              endIcon={renderSubmitButtonIcon()}
            >
              <T keyName="common.action.submit" />
            </Button>
          </Box>
        </Box>
      </FfairPaper>
    </FfairModal>
  );
};

export default ProductAttachmentsModal;
